<template>
  <div ref="root">
    <slot :open="open" />
  </div>
</template>

<script lang="ts">
import PhotoSwipe, { Item, Options as PhotoswipeOptions } from 'photoswipe';
import PhotoSwipeUI_Default from 'photoswipe/dist/photoswipe-ui-default';
import { Ref, defineComponent, ref } from 'vue';

export default defineComponent({
  setup() {
    const root: Ref<HTMLElement | null> = ref(null);

    const open = (index: number) => {
      if (root.value) {
        const pswp: HTMLElement | null = document.querySelector('.pswp');

        if (pswp) {
          const imgs = Array.from(root.value.querySelectorAll('img'));
          const options: PhotoswipeOptions = {
            history: false,
            index,
            closeOnScroll: false,
            closeOnVerticalDrag: false,
            getThumbBoundsFn(idx: number): { x: number; y: number; w: number } {
              const thumbnail = imgs[idx];

              const pageYScroll =
                window.pageYOffset || document.documentElement.scrollTop;

              const rect = thumbnail.getBoundingClientRect();

              return { x: rect.left, y: rect.top + pageYScroll, w: rect.width };
            },
          };
          const items = imgs.map(
            (img): Item => ({
              src: img.dataset.src,
              msrc: img.src,
              w: img.dataset.width
                ? parseInt(img.dataset.width, 10)
                : undefined,
              h: img.dataset.height
                ? parseInt(img.dataset.height, 10)
                : undefined,
            })
          );
          const photoswipe = new PhotoSwipe(
            pswp,
            PhotoSwipeUI_Default,
            items,
            options
          );
          photoswipe.init();
        }
      }
    };

    return { root, open };
  },
});
</script>

<style src="photoswipe/dist/photoswipe.css"></style>
<style lang="postcss">
.pswp__button {
  width: 44px;
  height: 44px;
  position: relative;
  background: none;
  cursor: pointer;
  overflow: visible;
  appearance: none;
  border: 0;
  padding: 0;
  margin: 0;
  float: right;
  opacity: 0.75;
  transition: opacity 0.2s;
  box-shadow: none;
  display: flex;
  justify-content: center;
  align-items: center;

  & svg {
    fill: theme('colors.white');
    pointer-events: none;
  }
}

.pswp__button:focus,
.pswp__button:hover {
  opacity: 1;
}

.pswp__button:active {
  outline: none;
  opacity: 0.9;
}

.pswp__button::-moz-focus-inner {
  padding: 0;
  border: 0;
}

/* pswp__ui--over-close class it added when mouse is over element that should close gallery */
.pswp__ui--over-close .pswp__button--close {
  opacity: 1;
}
/* no arrows on touch screens */
.pswp--touch .pswp__button--arrow--left,
.pswp--touch .pswp__button--arrow--right {
  visibility: hidden;
}
/* root element of UI */
.pswp__ui {
  -webkit-font-smoothing: auto;
  visibility: visible;
  opacity: 1;
  z-index: 1550;
}

/* top black bar with buttons and "1 of X" indicator */
.pswp__top-bar {
  position: absolute;
  left: 0;
  top: 0;
  height: 44px;
  width: 100%;
}

.pswp__top-bar,
.pswp--has_mouse .pswp__button--arrow--left,
.pswp--has_mouse .pswp__button--arrow--right {
  -webkit-backface-visibility: hidden;
  will-change: opacity;
  transition: opacity 333ms cubic-bezier(0.4, 0, 0.22, 1);
}

/* pswp--has_mouse class is added only when two subsequent mousemove events occur */
.pswp--has_mouse .pswp__button--arrow--left,
.pswp--has_mouse .pswp__button--arrow--right {
  visibility: visible;
}

/* pswp__ui--fit class is added when main image "fits" between top bar and bottom bar (caption) */
.pswp__ui--fit .pswp__top-bar,
.pswp__ui--fit .pswp__caption {
  background-color: rgba(0, 0, 0, 0.3);
}

/* pswp__ui--idle class is added when mouse isn't moving for several seconds (JS option timeToIdle) */
.pswp__ui--idle .pswp__top-bar {
  opacity: 0;
}

.pswp__ui--idle .pswp__button--arrow--left,
.pswp__ui--idle .pswp__button--arrow--right {
  opacity: 0;
}

/*
	pswp__ui--hidden class is added when controls are hidden
	e.g. when user taps to toggle visibility of controls
*/
.pswp__ui--hidden .pswp__top-bar,
.pswp__ui--hidden .pswp__caption,
.pswp__ui--hidden .pswp__button--arrow--left,
.pswp__ui--hidden .pswp__button--arrow--right {
  /* Force paint & create composition layer for controls. */
  opacity: 0.001;
}

/* pswp__ui--one-slide class is added when there is just one item in gallery */
.pswp__ui--one-slide .pswp__button--arrow--left,
.pswp__ui--one-slide .pswp__button--arrow--right,
.pswp__ui--one-slide .pswp__counter {
  display: none;
}

.pswp__element--disabled {
  display: none !important;
}

.pswp--minimal--dark .pswp__top-bar {
  background: none;
}
.pswp__button--zoom-out {
  display: none;
}
.pswp--zoomed-in {
  & .pswp__button--zoom-out {
    display: inline;
  }
  & .pswp__button--zoom-in {
    display: none;
  }
}
</style>
